import type { ProductTaxonomy } from '@/services/product-taxonomy';
import { getBaseEventGroups, type BaseEventGroup } from './base-events';

/**
 * The events a native data-out connector (mixpanel, amplitude) can subscribe to,
 * for the `/integrations/data_out/:type` config page.
 *
 * The allowlist below is what the connector accepts, and it currently covers the
 * whole base catalog — so this filter is a no-op today. It is kept explicit
 * anyway: adding a channel to the base catalog should not silently start
 * offering its events here, because a connector that cannot forward them would
 * accept the subscription and drop the traffic. `data-out-events.test.ts` fails
 * when the two sets diverge, which is the prompt to decide.
 *
 * Not to be confused with the Pipelines-source metric list in
 * `pages/workspaces/pipelines/sources/[source-id]/utils/message-metric-source-events.ts`
 * — a different surface with its own, older copy of this list.
 */
const DATA_OUT_EVENT_NAMES: ReadonlySet<string> = new Set([
  'customer_subscribed',
  'customer_unsubscribed',
  'customer_subscription_preferences_changed',
  'email_drafted',
  'email_attempted',
  'email_deferred',
  'email_sent',
  'email_delivered',
  'email_opened',
  'email_clicked',
  'email_converted',
  'email_unsubscribed',
  'email_bounced',
  'email_dropped',
  'email_spammed',
  'email_failed',
  'email_undeliverable',
  'in_app_drafted',
  'in_app_attempted',
  'in_app_sent',
  'in_app_opened',
  'in_app_clicked',
  'in_app_converted',
  'in_app_failed',
  'in_app_undeliverable',
  'push_drafted',
  'push_attempted',
  'push_sent',
  'push_delivered',
  'push_opened',
  'push_clicked',
  'push_converted',
  'push_bounced',
  'push_dropped',
  'push_failed',
  'push_undeliverable',
  'sms_drafted',
  'sms_attempted',
  'sms_sent',
  'sms_delivered',
  'sms_clicked',
  'sms_converted',
  'sms_bounced',
  'sms_dropped',
  'sms_failed',
  'sms_undeliverable',
  'sms_replied',
  'whatsapp_drafted',
  'whatsapp_attempted',
  'whatsapp_sent',
  'whatsapp_delivered',
  'whatsapp_opened',
  'whatsapp_clicked',
  'whatsapp_converted',
  'whatsapp_bounced',
  'whatsapp_dropped',
  'whatsapp_failed',
  'whatsapp_undeliverable',
  'whatsapp_replied',
  'slack_drafted',
  'slack_attempted',
  'slack_sent',
  'slack_clicked',
  'slack_failed',
  'webhook_drafted',
  'webhook_attempted',
  'webhook_sent',
  'webhook_clicked',
  'webhook_converted',
  'webhook_failed',
  'webhook_undeliverable',
]);

export { DATA_OUT_EVENT_NAMES };

/**
 * The base catalog narrowed to the data-out allowlist: groups with no supported
 * event drop out entirely, and surviving groups keep only supported items.
 * Group and item order follow the base catalog.
 */
export function getDataOutEventGroups(
  options: { productTaxonomy?: ProductTaxonomy } = {},
): readonly Readonly<BaseEventGroup>[] {
  return Object.freeze(
    getBaseEventGroups(options)
      .map((group) => ({ ...group, items: group.items.filter((item) => DATA_OUT_EVENT_NAMES.has(item.value)) }))
      .filter((group) => group.items.length > 0),
  );
}

export default getDataOutEventGroups();
